import {
  type Checks,
  checkCues,
  FRAME_RATES,
  frameRateRetime,
  NO_RETIME,
  parseRatio,
  parseSubtitles,
  parseTimeInput,
  type Retime,
  retimeCues,
  roundDiv,
  type SubtitleCue,
  type SubtitleFormat,
  shiftBy,
  stretchBy,
  syncRetime,
  type WriteOptions,
  writeSrt,
  writeVtt,
} from "@/lib/tool-kit/qualitative-text/subtitles";

export type TimingMode = "none" | "shift" | "stretch" | "fps" | "sync";

export interface ConvertSettings {
  target: SubtitleFormat;
  mode: TimingMode;
  /** Seconds to add to every time; may be negative or have a decimal part. */
  shift: string;
  /** Multiplier for every time, as a decimal. */
  factor: string;
  /** Frame-rate labels from `FRAME_RATES`. */
  fpsFrom: string;
  fpsTo: string;
  /** Where the first and last cue should start after the change. */
  syncFirst: string;
  syncLast: string;
  sort: boolean;
  write: WriteOptions;
}

export type ConvertProblem = "noCues" | "badValue";

export interface ConvertResult {
  problem: ConvertProblem | null;
  format: SubtitleFormat | null;
  /** The cues as read (sorted if asked). */
  before: SubtitleCue[];
  after: SubtitleCue[];
  /** Start of each cue in `after` before retiming. */
  originalStarts: number[];
  dropped: number;
  clamped: number;
  checks: Checks;
  output: string;
}

const MS_SECOND = 1000;

function buildRetime(
  settings: ConvertSettings,
  cues: readonly SubtitleCue[]
): Retime | null {
  switch (settings.mode) {
    case "none":
      return NO_RETIME;
    case "shift": {
      const seconds = parseRatio(settings.shift);
      return seconds
        ? shiftBy(roundDiv(seconds.num * MS_SECOND, seconds.den))
        : null;
    }
    case "stretch": {
      const factor = parseRatio(settings.factor);
      return factor ? stretchBy(factor) : null;
    }
    case "fps": {
      const from = FRAME_RATES.find((rate) => rate.label === settings.fpsFrom);
      const to = FRAME_RATES.find((rate) => rate.label === settings.fpsTo);
      return from && to ? frameRateRetime(from, to) : null;
    }
    default: {
      const starts = cues.map((cue) => cue.start);
      const first = parseTimeInput(settings.syncFirst);
      const last = parseTimeInput(settings.syncLast);
      if (first === null || last === null || starts.length < 2) {
        return null;
      }
      return syncRetime(Math.min(...starts), Math.max(...starts), first, last);
    }
  }
}

const EMPTY_CHECKS: Checks = {
  outOfOrder: 0,
  overlapping: 0,
  empty: 0,
  zeroLength: 0,
};

export function convertSubtitles(
  input: string,
  settings: ConvertSettings
): ConvertResult {
  const parsed = parseSubtitles(input);
  const read = settings.sort
    ? [...parsed.cues].sort((a, b) => a.start - b.start)
    : parsed.cues;
  const failure = (problem: ConvertProblem): ConvertResult => ({
    problem,
    format: parsed.format,
    before: read,
    after: [],
    originalStarts: [],
    dropped: 0,
    clamped: 0,
    checks: EMPTY_CHECKS,
    output: "",
  });
  if (read.length === 0) {
    return failure("noCues");
  }
  const retime = buildRetime(settings, read);
  if (!retime) {
    return failure("badValue");
  }
  const moved = retimeCues(read, retime);
  const writer = settings.target === "srt" ? writeSrt : writeVtt;
  return {
    problem: null,
    format: parsed.format,
    before: read,
    after: moved.cues,
    originalStarts: moved.originalStarts,
    dropped: moved.dropped,
    clamped: moved.clamped,
    checks: checkCues(moved.cues),
    output: writer(moved.cues, parsed.format, settings.write),
  };
}
